// components/lead-generation/scoring/scorers/ESGComplianceScorer.js

import { esgComplianceChecker } from '../../../../services/lead-generation/compliance/ESGComplianceChecker.js';
import { ESG_SCORING_WEIGHTS } from '../../../../config/business-types/esg-compliance.config.js';

export class ESGComplianceScorer {
  constructor(customerLocation = 'Nottingham, UK') {
    this.customerLocation = customerLocation;
    this.weights = ESG_SCORING_WEIGHTS;
  }

  /**
   * Calculate comprehensive ESG lead score
   * @param {Object} company - Company data
   * @returns {Object} Detailed scoring results
   */
  calculateScore(company) {
    // Get ESG compliance assessment
    const esgAssessment = esgComplianceChecker.assessESGRequirement(company);
    
    // Calculate individual scoring components
    const complianceScore = this.calculateComplianceScore(esgAssessment);
    const industryScore = this.calculateIndustryScore(esgAssessment.industryRisk);
    const sizeScore = this.calculateSizeScore(company);
    const proximityScore = this.calculateProximityScore(company.location);
    
    // Calculate weighted total score
    const totalScore = Math.round(
      (complianceScore * this.weights.complianceUrgency / 100) +
      (industryScore * this.weights.industryRisk / 100) +
      (sizeScore * this.weights.companySize / 100) +
      (proximityScore * this.weights.geographicProximity / 100)
    );

    return {
      totalScore: Math.min(totalScore, 100),
      breakdown: {
        compliance: {
          score: complianceScore,
          weight: this.weights.complianceUrgency,
          weighted: Math.round(complianceScore * this.weights.complianceUrgency / 100)
        },
        industry: {
          score: industryScore,
          weight: this.weights.industryRisk,
          weighted: Math.round(industryScore * this.weights.industryRisk / 100)
        },
        size: {
          score: sizeScore,
          weight: this.weights.companySize,
          weighted: Math.round(sizeScore * this.weights.companySize / 100)
        },
        proximity: {
          score: proximityScore,
          weight: this.weights.geographicProximity,
          weighted: Math.round(proximityScore * this.weights.geographicProximity / 100)
        }
      },
      esgAssessment,
      priority: this.determinePriority(totalScore, esgAssessment),
      reasoning: this.generateScoreReasoning(esgAssessment, totalScore)
    };
  }

  /**
   * Calculate compliance urgency score (0-100)
   */
  calculateComplianceScore(esgAssessment) {
    const { primaryRequirement, csrd, vsme } = esgAssessment;

    switch (primaryRequirement) {
      case 'CSRD_MANDATORY':
        // High score for mandatory compliance
        const urgencyMultiplier = {
          'IMMEDIATE': 100,
          'HIGH': 95,
          'MEDIUM': 80,
          'LOW': 70
        };
        return urgencyMultiplier[csrd.urgency] || 90;

      case 'VSME_VOLUNTARY':
        // Medium-high score for voluntary but eligible
        const baseVSMEScore = 70;
        const qualificationBonus = (vsme.qualificationScore / 100) * 15; // Up to 15 bonus points
        return Math.round(baseVSMEScore + qualificationBonus);

      default:
        // Low score for non-applicable companies
        return 25;
    }
  }

  /**
   * Calculate industry risk score (0-100)
   */
  calculateIndustryScore(industryRisk) {
    const riskScores = {
      'HIGH': 90,    // Manufacturing, energy, mining
      'MEDIUM': 70,  // Transport, construction, finance
      'LOW': 50,     // Services, technology
      'UNKNOWN': 40  // No industry data
    };

    return riskScores[industryRisk.level] || 40;
  }

  /**
   * Calculate company size score (0-100)
   * Larger companies = higher scores (more complex ESG needs)
   */
  calculateSizeScore(company) {
    const { employees, turnover } = company;
    
    let sizeScore = 30; // Base score
    
    // Employee-based scoring
    if (employees > 500) sizeScore += 40;
    else if (employees > 250) sizeScore += 35;
    else if (employees > 100) sizeScore += 30;
    else if (employees > 50) sizeScore += 25;
    else if (employees > 10) sizeScore += 15;
    
    // Revenue-based scoring
    if (turnover > 40000000) sizeScore += 30; // €40M+
    else if (turnover > 20000000) sizeScore += 25; // €20M+
    else if (turnover > 10000000) sizeScore += 20; // €10M+
    else if (turnover > 2000000) sizeScore += 15; // €2M+
    
    return Math.min(sizeScore, 100);
  }

  /**
   * Calculate geographic proximity score (0-100)
   */
  calculateProximityScore(companyLocation) {
    if (!companyLocation) return 30;
    
    const location = companyLocation.toLowerCase();
    
    // UK proximity scoring (adjust based on your customer's location)
    if (location.includes('nottingham')) return 100;
    if (location.includes('derby') || location.includes('leicester')) return 90;
    if (location.includes('birmingham') || location.includes('coventry')) return 85;
    if (location.includes('london')) return 70;
    if (location.includes('manchester') || location.includes('leeds')) return 75;
    if (location.includes('uk') || location.includes('united kingdom')) return 60;
    
    // European proximity scoring
    if (location.includes('ireland')) return 65;
    if (location.includes('netherlands') || location.includes('belgium')) return 55;
    if (location.includes('germany') || location.includes('france')) return 50;
    if (location.includes('europe') || location.includes('eu')) return 45;
    
    return 30; // Rest of world
  }

  /**
   * Determine overall lead priority
   */
  determinePriority(totalScore, esgAssessment) {
    const { primaryRequirement } = esgAssessment;
    
    // CSRD mandatory companies are always high priority regardless of score
    if (primaryRequirement === 'CSRD_MANDATORY') {
      return totalScore >= 70 ? 'CRITICAL' : 'HIGH';
    }
    
    // Score-based priority for others
    if (totalScore >= 80) return 'HIGH';
    if (totalScore >= 60) return 'MEDIUM';
    if (totalScore >= 40) return 'LOW';
    return 'MINIMAL';
  }

  /**
   * Generate human-readable scoring reasoning
   */
  generateScoreReasoning(esgAssessment, totalScore) {
    const reasons = [];
    const { primaryRequirement, industryRisk, csrd, vsme } = esgAssessment;
    
    // Primary requirement reasoning
    switch (primaryRequirement) {
      case 'CSRD_MANDATORY':
        reasons.push(`Mandatory CSRD compliance required by ${csrd.deadline}`);
        if (csrd.urgency === 'IMMEDIATE' || csrd.urgency === 'HIGH') {
          reasons.push('Urgent implementation timeline increases opportunity value');
        }
        break;
        
      case 'VSME_VOLUNTARY':
        reasons.push(`VSME-eligible company (${Math.round(vsme.qualificationScore)}% threshold match)`);
        reasons.push('Voluntary ESG reporting provides competitive advantage');
        break;
        
      default:
        reasons.push('Limited ESG compliance requirements currently');
        break;
    }
    
    // Industry risk reasoning
    if (industryRisk.level === 'HIGH') {
      reasons.push(`High ESG risk industry (${industryRisk.category}) requires comprehensive reporting`);
    } else if (industryRisk.level === 'MEDIUM') {
      reasons.push(`Medium ESG risk industry with growing reporting expectations`);
    }
    
    // Overall assessment
    if (totalScore >= 80) {
      reasons.push('High-value lead with strong ESG software opportunity');
    } else if (totalScore >= 60) {
      reasons.push('Solid lead with good ESG software potential');
    } else if (totalScore >= 40) {
      reasons.push('Moderate opportunity requiring nurture approach');
    } else {
      reasons.push('Low immediate opportunity, consider long-term relationship');
    }
    
    return reasons;
  }

  /**
   * Generate ESG-specific insights for the lead
   */
  generateESGInsights(company, scoringResult) {
    const { esgAssessment } = scoringResult;
    const insights = [];
    
    // Compliance-specific insights
    if (esgAssessment.primaryRequirement === 'CSRD_MANDATORY') {
      insights.push({
        type: 'REGULATORY_DEADLINE',
        title: 'CSRD Compliance Deadline',
        description: `Company must comply with CSRD reporting by ${esgAssessment.csrd.deadline}`,
        urgency: esgAssessment.csrd.urgency,
        actionRequired: 'Immediate ESG system implementation needed'
      });
    }
    
    if (esgAssessment.primaryRequirement === 'VSME_VOLUNTARY') {
      insights.push({
        type: 'COMPETITIVE_ADVANTAGE',
        title: 'VSME Opportunity',
        description: 'Company qualifies for voluntary VSME reporting framework',
        urgency: 'MEDIUM',
        actionRequired: 'Position ESG reporting as competitive differentiator'
      });
    }
    
    // Industry-specific insights
    if (esgAssessment.industryRisk.level === 'HIGH') {
      insights.push({
        type: 'INDUSTRY_RISK',
        title: 'High ESG Risk Sector',
        description: `${esgAssessment.industryRisk.category} faces increased ESG scrutiny`,
        urgency: 'HIGH',
        actionRequired: 'Industry-specific ESG framework implementation'
      });
    }
    
    // Business value insights
    const businessValue = esgAssessment.businessValue;
    insights.push({
      type: 'BUSINESS_VALUE',
      title: 'Estimated Opportunity Value',
      description: `Estimated ESG software opportunity: €${businessValue.estimated.toLocaleString()}`,
      urgency: businessValue.confidence === 'HIGH' ? 'HIGH' : 'MEDIUM',
      actionRequired: `Target timeline: ${businessValue.timeframe}`
    });
    
    return insights;
  }
}

// Export singleton instance for ESG customers
export const esgScorer = new ESGComplianceScorer();